/**
 * The shared rail law (M11) — ONE truth for the client driver, the headless
 * survivability tests, and the phys-debug tools.
 *
 * A rail is a dense polyline over a segment's world-space path (points,
 * tangents, cumulative s) with a per-station physics modifier resolved from
 * the per-tile channel: piece families carry movement character (helix =
 * banked spiral, ride it faster; chutes pull harder) and planner set-piece
 * zones outrank families (a boost-chute hurls; a jump gap is hands-off
 * ballistic flight).
 *
 * `stationForce` is the entire per-step control law: nearest-station
 * lookahead (steepest climb ahead + curvature ahead), the assist force, and
 * the modifier extras (gravity scaling), combined into one world-space
 * force vector. Drivers differ only in how they find the nearest station.
 */
import type { LoopPath } from "../../shared/types.ts";
import { segLength, segPoint } from "../../gen/geom.ts";
import { assistForce, GRAVITY_Y, MARBLE_RESTITUTION } from "./engine.ts";
import {
  type Affine,
  affineApply,
  isIdentityAffine,
  quatRotate,
  type SegmentFrame,
  segmentFrameAt,
  twistParam,
} from "../../shared/frame.ts";

/** Resolved per-station physics modifier. */
export interface RailMod {
  /** Scales the assist's target/safe speeds (and its authority). */
  speed?: number;
  /** Lateral centering spring toward the rail line (split windows: a
   * marble still oscillating from the branch bend wedges on the merge
   * wedge-slab edge — fuzz-0 parked at the mouth seam). Boost zones get
   * this implicitly via speed > 1. */
  center?: boolean;
  /** Gravity multiplier while on the tile (chutes pull harder). */
  grav?: number;
  /** Ballistic flight (jump gaps): the assist is hands-off entirely. */
  flight?: boolean;
  /** Flight gravity scale (propeller zones: Marble Blast helicopter —
   * an upward force cancels this fraction of gravity mid-flight). */
  gravScale?: number;
  /** Marble restitution override (shock-absorber zones at hard landings). */
  restitution?: number;
  /** Low-grip surface (icy zones): the assist's authority drops to a
   * fraction — the marble carries its momentum and slides. */
  icy?: boolean;
  /** Gravity-rotation zones (M13): world gravity TILTS forward along the
   * station's travel direction by this many radians — the void's gravity
   * leans and the marble surges down a run that is geometrically flat.
   * The driver applies it via PhysicsEngine.setGravity per step. */
  tilt?: number;
}

export interface Rail {
  pts: [number, number, number][];
  tans: [number, number, number][];
  /** Cumulative arc length, starting at 0 for this rail. */
  s: number[];
  mods: (RailMod | null)[];
  /** Authoring provenance aligned 1:1 with the dense stations. Debug
   * recovery logs use this to name the exact realized tile/window that
   * handed a marble to the kinematic rail or respawn net. Older synthetic
   * test rails may omit it. */
  sources?: RailStationSource[];
  /** Gravity-reorientation frames (M16): pts/tans/mods stay in PLAN
   * space (+y up — the control laws' one truth); each station's affine
   * maps plan → presented world. Undefined = identity (the common case);
   * a single Affine = the whole rail shares one frame; an array = a
   * twist zone varies along the rail. Drivers conjugate marble state
   * through the frame — the autopilot never sees a wall or ceiling. */
  frames?: Affine | (Affine | null)[];
  /** Cached presented-world station points (filled by attachFrames) —
   * nearest-station search and respawns compare in world space. */
  worldPts?: [number, number, number][];
}

/** Authored source of one dense rail station. */
export interface RailStationSource {
  /** Path/window index within the endless segment. */
  window: number;
  /** Generator family selected for that window (straight, slant, ...). */
  family: string;
  /** Planner set-piece tag, including a per-part alt-lane override. */
  zone: string | null;
  /** Index into the segment's authored placements, or -1 for shared mesh. */
  placementIndex: number;
  /** Concrete GLB piece id when the placement is addressable. */
  pieceId: string | null;
  /** Whether this station follows the alternate branch/lane. */
  alt: boolean;
  /** Curve-part index within the selected window/lane. */
  part: number;
}

/** Attach a segment's presentation frames to its rail: per-station
 * affines (a partial roll inside the twist run) + cached world points.
 * Rails without reorient history skip this entirely. */
export function attachFrames(rail: Rail, f: SegmentFrame): void {
  if (!f.twist) {
    if (isIdentityAffine(f.entry)) return;
    rail.frames = f.entry;
    rail.worldPts = rail.pts.map((p) =>
      affineApply(f.entry, p) as [number, number, number]
    );
    return;
  }
  const frames: Affine[] = [];
  const worldPts: [number, number, number][] = [];
  for (const p of rail.pts) {
    const fr = segmentFrameAt(f, twistParam(f, p));
    frames.push(fr);
    worldPts.push(affineApply(fr, p) as [number, number, number]);
  }
  rail.frames = frames;
  rail.worldPts = worldPts;
}

/** Station frame (plan → world), or null for identity. */
export function railFrameAt(rail: Rail, idx: number): Affine | null {
  const f = rail.frames;
  if (!f) return null;
  if (Array.isArray(f)) return f[idx] ?? null;
  return isIdentityAffine(f) ? null : f;
}

/** Station point in presented WORLD space (respawns, camera, streaming). */
export function stationWorldPoint(
  rail: Rail,
  idx: number,
  out: [number, number, number] = [0, 0, 0],
): [number, number, number] {
  const w = rail.worldPts?.[idx];
  if (w) {
    out[0] = w[0];
    out[1] = w[1];
    out[2] = w[2];
    return out;
  }
  const p = rail.pts[idx];
  const f = railFrameAt(rail, idx);
  if (!f) {
    out[0] = p[0];
    out[1] = p[1];
    out[2] = p[2];
    return out;
  }
  return affineApply(f, p, out);
}

/** Station tangent in presented WORLD space. */
export function stationWorldTan(
  rail: Rail,
  idx: number,
  out: [number, number, number] = [0, 0, 0],
): [number, number, number] {
  const t = rail.tans[idx];
  const f = railFrameAt(rail, idx);
  if (!f) {
    out[0] = t[0];
    out[1] = t[1];
    out[2] = t[2];
    return out;
  }
  return quatRotate(f.q, t, out);
}

/**
 * Per-tile movement character by piece family (Marble Blast-inspired).
 * The same channel carries icy/boost/bouncer surfaces and island gravity
 * rotation later.
 */
const TILE_MODS: Record<string, RailMod> = {
  helix: { speed: 1.35 },
  slant: { grav: 1.2 },
  ramp: { grav: 1.15 },
  // The rail follows the branch lane through a diamond, so centering onto
  // it is correct along the whole window — and it keeps merge arrivals off
  // the wedge-slab edge seam.
  split: { center: true },
};

/** Planner zone kinds → rail modifiers. Zones outrank family character. */
const ZONE_MODS: Record<string, RailMod> = {
  boost: { speed: 1.9, grav: 1.2 },
  jump: { flight: true },
  // Propeller-jump: the helicopter trick — gravity drops to 0.35 during
  // the (longer) flight, so the same launch speed clears a 2-cell gap.
  propeller: { flight: true, gravScale: 0.35 },
  // Shock absorber at hard-landing zones (Marble Blast: 0.01) — the
  // driver applies it to the marble while the nearest station is inside.
  shock: { restitution: 0.02 },
  // Icy patch: grip is what the assist models — on ice it barely has any.
  icy: { icy: true },
  // Gravity rotation: ~17° forward lean (20·sin ≈ 5.9 u/s² of pull on a
  // flat run). The assist stays silent between cruise and the 9 u/s
  // envelope cap, so gravity itself is what hurls the marble — and the
  // centering spring keeps the surge straight.
  gravity: { tilt: 0.3, center: true },
  // Takeable spur window: centering keeps main-line marbles out of the
  // branch mouth (the composite opens one wall there) and branch riders
  // square on the diving board.
  spur: { center: true },
  // Braid fork/merge junctions (M17): same law — main-lane marbles stay
  // out of the open branch mouths, alt riders hold their pull-off line.
  // (The fork window's mod rides the WHOLE alt chain for alt riders —
  // centering keeps them on the wandering lane.)
  braid: { center: true },
  // Reorient run (M16): the channel corkscrews around the centerline —
  // plan-space centering (rotated to world by the frame) holds the
  // marble on the line while the floor rolls under it.
  reorient: { center: true },
};

/** The station spacing every driver samples at. */
export const RAIL_STEP = 0.5;

/** The segment fields the rail law needs (EndlessSegment satisfies it). */
export interface RailSource {
  path: LoopPath;
  pieceFamilies: string[];
  windowMods: (string | null)[];
  /** Optional for small synthetic law tests; EndlessSegment supplies it. */
  placements?: readonly { pieceId: string }[];
}

/** Path-segment indices that are jump gaps — the collision proxies emit no
 * channel there (absence of geometry IS the gap). The law lives with the
 * proxy builder (T7 parity); re-exported here for the drivers. */
export { gapSegmentsOf } from "../../gen/endless/proxies.ts";

/** True if any window in the segment carries a diamond's other branch. */
export function hasAltBranch(seg: RailSource): boolean {
  return seg.path.segments.some((ls) => ls.alt !== undefined);
}

/** True if the segment hosts a braid (M17). Braid segments use PER-RACER
 * lane choice — the client's global respawn branch-flip (branchOf) must
 * ignore them (that machinery stays for diamonds/spurs). */
export function hasBraid(seg: RailSource): boolean {
  return seg.path.segments.some((ls) => ls.alt?.braid === true);
}

/** Dense rail for one segment (local s from 0). `useAlt` rides every
 * diamond's OTHER branch — the respawn-variety rail. A takeable SPUR's
 * alt ends diving through the hole; the stations then SKIP the main
 * hook and resume at the receiver window passing directly under the
 * hole (Δ 1.0 by construction) — station order matches what the falling
 * marble physically does, so the wishdir look target never points back
 * up at geometry the rider skipped. */
export function railForSegment(seg: RailSource, useAlt = false): Rail {
  const rail: Rail = { pts: [], tans: [], s: [], mods: [], sources: [] };
  let acc = 0;
  /** Set after a spur dive: skip windows until one passes this point. */
  let resumeNear: readonly [number, number, number] | null = null;
  /** Set after a braid alt chain (which runs through to the MERGE exit
   * port): skip the main windows underneath the braid until the window
   * that STARTS at that port — ports are exact, so the match is tight. */
  let resumeAtStart: readonly [number, number, number] | null = null;
  const approaches = (
    curve: readonly LoopPath["segments"][number]["curve"][number][],
    p: readonly [number, number, number],
  ): boolean =>
    curve.some((part) =>
      [0, 0.5, 1].some((t) => {
        const q = segPoint(part, t);
        return Math.hypot(q[0] - p[0], q[1] - p[1], q[2] - p[2]) < 0.6;
      })
    );
  for (const [lsIdx, ls] of seg.path.segments.entries()) {
    if (resumeAtStart) {
      const p0 = segPoint(ls.curve[0], 0);
      if (
        Math.hypot(
          p0[0] - resumeAtStart[0],
          p0[1] - resumeAtStart[1],
          p0[2] - resumeAtStart[2],
        ) > 0.3
      ) continue;
      resumeAtStart = null;
    }
    if (resumeNear) {
      if (!approaches(ls.curve, resumeNear)) continue;
      resumeNear = null;
    }
    const zone = seg.windowMods[lsIdx];
    const mod = (zone ? ZONE_MODS[zone] : undefined) ??
      TILE_MODS[seg.pieceFamilies[lsIdx]] ?? null;
    const alt = useAlt && ls.alt;
    const parts = alt ? ls.alt!.curve : ls.curve;
    for (const [partIdx, part] of parts.entries()) {
      // Braid lane mods (M19b): per-part zone tags OVERLAY the host
      // window's mod on the alt chain — the fork's centering still rides
      // the whole lane (keeps wishdir riders on the wander), the tag
      // adds its surface on top. Untagged chains (diamonds, spurs, V1
      // braids) are byte-identical to before.
      const altZone = alt ? ls.alt!.zones?.[partIdx] : undefined;
      const partMod = altZone ? { ...(mod ?? {}), ...ZONE_MODS[altZone] } : mod;
      const plen = segLength(part);
      const steps = Math.max(1, Math.ceil(plen / RAIL_STEP));
      for (let j = 0; j < steps; j++) {
        const a = segPoint(part, j / steps);
        const b = segPoint(part, Math.min(1, (j + 1) / steps));
        const d = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
        const l = Math.hypot(d[0], d[1], d[2]) || 1;
        rail.pts.push([a[0], a[1], a[2]]);
        rail.tans.push([d[0] / l, d[1] / l, d[2] / l]);
        rail.s.push(acc);
        rail.mods.push(partMod);
        rail.sources!.push({
          window: lsIdx,
          family: seg.pieceFamilies[lsIdx] ?? "unknown",
          zone: altZone ?? zone ?? null,
          placementIndex: ls.placementIndex,
          pieceId: ls.placementIndex >= 0
            ? seg.placements?.[ls.placementIndex]?.pieceId ?? null
            : null,
          alt: Boolean(alt),
          part: partIdx,
        });
        acc += l;
      }
    }
    if (alt && ls.alt!.spur) {
      const lastPart = ls.alt!.curve[ls.alt!.curve.length - 1];
      const tip = segPoint(lastPart, 1);
      resumeNear = [tip[0], tip[1], tip[2]];
    } else if (alt && ls.alt!.braid) {
      const lastPart = ls.alt!.curve[ls.alt!.curve.length - 1];
      const tip = segPoint(lastPart, 1);
      resumeAtStart = [tip[0], tip[1], tip[2]];
    }
  }
  return rail;
}

/** Chain per-segment rails into one (headless drivers). Presentation
 * frames (M16) survive the concat: any framed input promotes the output
 * to per-station frames (null = identity) + world points. */
export function concatRails(rails: readonly Rail[]): Rail {
  const sourced = rails.every((r) => r.sources !== undefined);
  const out: Rail = {
    pts: [],
    tans: [],
    s: [],
    mods: [],
    sources: sourced ? [] : undefined,
  };
  let base = 0;
  const framed = rails.some((r) => r.frames !== undefined);
  if (framed) {
    out.frames = [];
    out.worldPts = [];
  }
  for (const r of rails) {
    out.pts.push(...r.pts);
    out.tans.push(...r.tans);
    for (const s of r.s) out.s.push(base + s);
    out.mods.push(...r.mods);
    if (sourced) out.sources!.push(...r.sources!);
    if (framed) {
      const frames = out.frames as (Affine | null)[];
      for (let i = 0; i < r.pts.length; i++) {
        frames.push(railFrameAt(r, i));
        out.worldPts!.push(
          r.worldPts?.[i] ?? (r.pts[i].slice() as [number, number, number]),
        );
      }
    }
    if (r.s.length > 0) base += r.s[r.s.length - 1] + RAIL_STEP;
  }
  return out;
}

/** Marble restitution the station calls for (shock zones override the
 * default; drivers pass this to PhysicsEngine.setRestitution per step —
 * the engine caches, so repeats are free). */
export function stationRestitution(rail: Rail, idx: number): number {
  return rail.mods[idx]?.restitution ?? MARBLE_RESTITUTION;
}

/** World gravity vector the station calls for (gravity-rotation zones
 * tilt it forward along the station's horizontal travel direction; every
 * other station keeps plain -Y). Drivers pass this to
 * PhysicsEngine.setGravity per step — the engine caches. */
export function stationGravity(
  rail: Rail,
  idx: number,
): [number, number, number] {
  const tilt = rail.mods[idx]?.tilt;
  const t = rail.tans[idx];
  if (!tilt || !t) return [0, GRAVITY_Y, 0];
  const hl = Math.hypot(t[0], t[2]);
  if (hl < 1e-6) return [0, GRAVITY_Y, 0];
  const g = -GRAVITY_Y; // magnitude
  const fwd = g * Math.sin(tilt) / hl;
  return [t[0] * fwd, GRAVITY_Y * Math.cos(tilt), t[2] * fwd];
}

/** Per-marble gravity-zone force (M15): with N marbles in one world the
 * global gravity vector stays fixed at [0, GRAVITY_Y, 0]; a marble whose
 * station calls for tilted gravity gets m·(g_zone − g_world) as an extra
 * force — identical acceleration, correct per marble. Reoriented frames
 * (M16) compose here: the zone gravity (plan space, including any tilt)
 * rotates through the station frame, so a wall-riding marble is pulled
 * INTO its local floor while world gravity stays put. */
export function stationGravityDelta(
  rail: Rail,
  idx: number,
  mass: number,
): [number, number, number] | null {
  const g = stationGravity(rail, idx);
  const f = railFrameAt(rail, idx);
  if (f) quatRotate(f.q, g, g);
  if (g[0] === 0 && g[1] === GRAVITY_Y && g[2] === 0) return null;
  return [g[0] * mass, (g[1] - GRAVITY_Y) * mass, g[2] * mass];
}

/** Stations of rail lookahead (≈4 world units at RAIL_STEP). */
const LOOKAHEAD = 8;

/**
 * The complete per-step control law at a station: assist toward the local
 * tangent (sized by the steepest climb ahead, braked by the curvature
 * ahead, scaled by the station's modifier) plus modifier gravity extras,
 * plus a lateral centering spring in boost zones — a launch is only as
 * straight as its run-up, and a marble still oscillating from an earlier
 * bend leaves the lip diagonally and misses any catch (abs-1: −1.9 u/s
 * lateral at launch drifted 1.4 world off the pan). Returns one
 * world-space force or null. `nextRail` chains lookahead across a
 * segment boundary (client drivers keep per-segment rails).
 */
export function stationForce(
  rail: Rail,
  idx: number,
  pos: readonly [number, number, number],
  vel: readonly [number, number, number],
  mass: number,
  nextRail: Rail | null = null,
): [number, number, number] | null {
  const tanAt = (i: number): [number, number, number] | null =>
    rail.tans[i] ??
      (nextRail ? nextRail.tans[i - rail.tans.length] ?? null : null);
  const t = rail.tans[idx];
  if (!t) return null;
  const mod = rail.mods[idx];
  // Jump gaps: ballistic flight — no assist (the launch speed was priced
  // at plan time; steering mid-flight would change where it lands). A
  // propeller zone's gravity scaling is part of that pricing: a constant
  // upward force cancels (1 − gravScale) of gravity for a longer, floatier
  // arc over a wider gap.
  if (mod?.flight) {
    if (mod.gravScale !== undefined && mod.gravScale !== 1) {
      return [0, (1 - mod.gravScale) * -GRAVITY_Y * mass, 0];
    }
    return null;
  }
  let climbTy = t[1];
  let curveAhead = 0;
  for (let k = 1; k <= LOOKAHEAD; k++) {
    const look = tanAt(idx + k);
    const prev = tanAt(idx + k - 1);
    if (!look || !prev) break;
    climbTy = Math.max(climbTy, look[1]);
    const dot = Math.min(
      1,
      look[0] * prev[0] + look[1] * prev[1] + look[2] * prev[2],
    );
    curveAhead += Math.acos(Math.max(-1, dot));
  }
  const f = assistForce(vel, t, mass, climbTy, curveAhead, mod?.speed ?? 1) ??
    [0, 0, 0];
  // Icy: the assist is grip — on ice it keeps only a sliver of authority
  // (enough that the line still progresses; the slide is the spectacle).
  const grip = mod?.icy ? 0.3 : 1;
  const out: [number, number, number] = [
    f[0] * grip,
    f[1] * grip,
    f[2] * grip,
  ];
  // Lateral centering in boost zones (straight by construction) and split
  // windows: a spring-damper toward the rail line kills wall-to-wall
  // oscillation so the marble launches true / merges clean.
  if ((mod?.speed && mod.speed > 1) || mod?.center) {
    const r = rail.pts[idx];
    const nl = Math.hypot(t[2], t[0]) || 1;
    const ux = t[2] / nl;
    const uz = -t[0] / nl;
    const off = (pos[0] - r[0]) * ux + (pos[2] - r[2]) * uz;
    const lvel = vel[0] * ux + vel[2] * uz;
    const a = Math.max(-14, Math.min(14, -40 * off - 12 * lvel));
    out[0] += ux * a * mass;
    out[2] += uz * a * mass;
  }
  // Extra pull ONLY on descending stations — grav mods mean "chutes pull
  // harder", never "fight the climb carry" (a ramp tile is the same family
  // going up; extra gravity there wedged marbles at climb bases).
  if (mod?.grav && mod.grav !== 1 && t[1] < -0.05 && climbTy < 0.12) {
    out[1] += (mod.grav - 1) * GRAVITY_Y * mass;
  }
  return out[0] === 0 && out[1] === 0 && out[2] === 0 ? null : out;
}
